import {createContext, FC, ReactNode, useCallback, useContext, useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useAuth} from '../../../modules/auth'
import {getNuklieStatus, NuklieResource, NuklieStatus} from '../../../services/nuklieService'
import {NuklieInboxModal, NuklieInboxRequest} from './NuklieInboxModal'
import {NuklieFloatingButton} from './NuklieFloatingButton'

export const NUKLIE_STATUS_QUERY = ['nuklieStatus']

type NuklieContextValue = {
  // Integração ativada e usuário com acesso (só EMPRESA)
  enabled: boolean
  canManage: boolean
  status?: NuklieStatus
  openInbox: () => void
  openConversation: (resource: NuklieResource, id: string) => void
}

const NuklieContext = createContext<NuklieContextValue>({
  enabled: false,
  canManage: false,
  openInbox: () => {},
  openConversation: () => {},
})

export const useNuklie = () => useContext(NuklieContext)

export const NuklieProvider: FC<{children: ReactNode}> = ({children}) => {
  const {currentUser} = useAuth()
  const canManage = currentUser?.role?.slug === 'EMPRESA'
  const [request, setRequest] = useState<NuklieInboxRequest | null>(null)

  const {data: status} = useQuery({
    queryKey: [...NUKLIE_STATUS_QUERY, currentUser?.id],
    queryFn: getNuklieStatus,
    enabled: canManage,
    staleTime: 1000 * 60 * 10,
    retry: false,
  })

  const enabled = canManage && !!status?.active && !!status.integration_public_identifier

  // Cada abertura é um pedido novo: a caixa é remontada do zero (com ou sem contato).
  const openInbox = useCallback(() => setRequest({key: Date.now()}), [])
  const openConversation = useCallback(
    (resource: NuklieResource, id: string) => setRequest({key: Date.now(), resource, id}),
    []
  )

  const value = useMemo(
    () => ({enabled, canManage, status, openInbox, openConversation}),
    [enabled, canManage, status, openInbox, openConversation]
  )

  return (
    <NuklieContext.Provider value={value}>
      {children}
      {enabled && status && (
        <>
          <NuklieFloatingButton onClick={openInbox} />
          {request && (
            <NuklieInboxModal
              status={status}
              request={request}
              userEmail={currentUser?.email}
              onClose={() => setRequest(null)}
            />
          )}
        </>
      )}
    </NuklieContext.Provider>
  )
}
